/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useState} from 'react';
import {Devices} from '@ion/core/src/types/entities';
import {useGridSelection, UseGridSelectionProps} from './useGridSelection';

export interface UseDeviceGridSelectionProps extends UseGridSelectionProps {
  selectedDevices?: Devices;
};

/**
 * React Hook to provide local selector/state for device grid selection functionality.
 *
 *  ```typescript
 *   import {useDeviceGridSelection} from '@ion/applications/src/hooks/';
 *
 *   const {
 *     selectedDevices,
 *     setSelectedDevices,
 *     areTotalRowsSelected,
 *     setAreTotalRowsSelected,
 *     totalRowsCount,
 *     setTotalRowsCount,
 *     filters,
 *     setFilters,
 *     search,
 *     setSearch,
 *     includeSubAccounts,
 *     setIncludeSubAccounts
 *   } = useDeviceGridSelection();
 *  ```
 */
export const useDeviceGridSelection = ({
  selectedDevices      : originalSelectedDevices,
  areTotalRowsSelected : originalAreTotalRowsSelected,
  totalRowsCount       : originalTotalRowsCount,
  filters              : originalFilters,
  search               : originalSearch,
  includeSubAccounts   : originalIncludeSubAccounts
}: UseDeviceGridSelectionProps) => {
  const [selectedDevices, setSelectedDevices] = useState<Devices>(originalSelectedDevices || []);

  const {
    areTotalRowsSelected,
    setAreTotalRowsSelected,
    totalRowsCount,
    setTotalRowsCount,
    filters,
    setFilters,
    search,
    setSearch,
    includeSubAccounts,
    setIncludeSubAccounts
  } = useGridSelection({
    areTotalRowsSelected : originalAreTotalRowsSelected,
    totalRowsCount       : originalTotalRowsCount,
    filters              : originalFilters,
    search               : originalSearch,
    includeSubAccounts   : originalIncludeSubAccounts
  });

  return {
    selectedDevices,
    setSelectedDevices,
    areTotalRowsSelected,
    setAreTotalRowsSelected,
    totalRowsCount,
    setTotalRowsCount,
    filters,
    setFilters,
    search,
    setSearch,
    includeSubAccounts,
    setIncludeSubAccounts
  };
};

export default useDeviceGridSelection;
